import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { FluentProvider, Spinner } from '@fluentui/react-components';
import { QueryClient } from '@tanstack/react-query';
import { ptcLightTheme } from './theme';
import { isPublicFormPath } from './public-form/publicEntry';
import './index.css';

// WS-G: branch BEFORE the MSAL auth gate. A client arriving at /f/{token} gets a standalone,
// lazy-loaded public form (no MSAL, no shell); everyone else gets the lazy staff tree. React.lazy
// keeps the two as separate chunks — a phone never downloads the CRM bundle and staff never
// download the form code.
const PublicFormApp = lazy(() => import('./public-form/PublicFormApp'));
const AuthedApp = lazy(() => import('./AuthedApp'));

const queryClient = new QueryClient({
  // gcTime (WS-D Task 9): keep unmounted query data cached for 30 minutes so revisits
  // paint the last frame instantly and revalidate silently (staleTime stays 30s).
  defaultOptions: { queries: { staleTime: 30_000, gcTime: 30 * 60_000, retry: 1 } }
});

const isPublic = isPublicFormPath(window.location.pathname);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Suspense
      fallback={
        <FluentProvider theme={ptcLightTheme}>
          <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner />
          </div>
        </FluentProvider>
      }
    >
      {isPublic ? <PublicFormApp /> : <AuthedApp queryClient={queryClient} />}
    </Suspense>
  </StrictMode>
);
